import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core';
import { Kit, KitsReadModel } from './kit';
import { DocumentFilterItem, DocumentFilterItemReadModel } from '../../documents/document-filter-item';
import { FilterFieldValue } from '../../covance-filter';
import { prepareFilterUrlParams } from '../../documents/prepare-filter-url-params';
import { KitDetailReadModel, KitDetail } from './kit-detail';
import { SponsorStudyKitReport, SponsorStudyKitReportReadModel } from '../../sponsor/kit-inventory/sponsor-study-kit-report';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { KitExpiration, SponsorStudyKitExpirationReadModel } from '../../sponsor/kit-inventory/kit-expiration-report';
import { Protocol } from '../../admin/shared';


@Injectable()
export class KitInventoryStorage {

    constructor(
        protected settings: Settings,
        protected http: HttpClient,
    ) {}

    //investigator
    public getKits(site: number, filterParameters: FilterFieldValue[] = null): Observable<Kit[]> {
        let filtersParams = prepareFilterUrlParams(filterParameters);

        return this.http.get(`${this.settings.apiUrl}/investigator/sites/${site}/kits`, { params: filtersParams })
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, KitsReadModel).kits;
                    return kitData;
                }
                return null;
            })
        );
    }

    //investigator, sponsor, admin
    public getKitDetails(site: number, ctSiteId: number, kitTypeId: number): Observable<KitDetail[]> {

        return this.http.get(`${this.settings.apiUrl}/investigator/sites/${site}/kits/${ctSiteId}/${kitTypeId}`)
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, KitDetailReadModel).kitDetails;
                    return kitData;
                }
                return null;
            })
        );
    }

    //investigator
    public loadFilterItems(site: number): Observable<DocumentFilterItem[]> {
        return this.http.get(
            `${this.settings.apiUrl}/investigator/sites/${site}/kits/filter-items`
        ).pipe(
            map((r) => deserialize(r, DocumentFilterItemReadModel).documentFilterItems)
        );
    }

    //sponsor
    public loadSponsorFilterItems(study: number): Observable<DocumentFilterItem[]> {
        return this.http.get(
            `${this.settings.apiUrl}/sponsor/study/${study}/kit-report/filter-items`
        ).pipe(
            map((r) => deserialize(r, DocumentFilterItemReadModel).documentFilterItems)
        );
    }

    //sponsor
    public loadSponsorExpirationFilterItems(study: number): Observable<DocumentFilterItem[]> {
        return this.http.get(
            `${this.settings.apiUrl}/sponsor/study/${study}/kit-expire/filter-items`
        ).pipe(
            map((r) => deserialize(r, DocumentFilterItemReadModel).documentFilterItems)
        );
    }

    //sponsor
    public getKitReports(study: SponsorStudy, filterParameters: FilterFieldValue[] = null): Observable<SponsorStudyKitReport[]> {
        let filtersParams = prepareFilterUrlParams(filterParameters, true); 

        return this.http.get(`${this.settings.apiUrl}/sponsor/study/${study.protocolId}/kit-report`, { params: filtersParams })
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, SponsorStudyKitReportReadModel).kitReports;
                    return kitData;
                }
                return null;
            })
        );
    }

    //sponsor
    public getSponsorKitExpirationReport(study: SponsorStudy, filterParameters: FilterFieldValue[] = null): Observable<KitExpiration[]> {
        let filtersParams = prepareFilterUrlParams(filterParameters, true);
        if (filtersParams && !filterParameters[4].value) {
            filtersParams = filtersParams.append('requestLimit', this.settings.maxNumberOfSponsorLabReports.toString());
        }

        return this.http.get(`${this.settings.apiUrl}/sponsor/study/${study.protocolId}/kit-expire`, { params: filtersParams })
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, SponsorStudyKitExpirationReadModel).kitExpirations;
                    return kitData;
                }
                return null;
            })
            );
    }

    //admin
    public getAdminKitReports(protocol: Protocol[], filterParameters: FilterFieldValue[] = null): Observable<SponsorStudyKitReport[]> {
        let filtersParams = prepareFilterUrlParams(filterParameters, true);
        let studyId = 0;
        if (protocol) {
            studyId = protocol[0].id;
        }

        return this.http.get(`${this.settings.apiUrl}/admin/kit-reports/inventory/${encodeURI(studyId.toString())}`, { params: filtersParams })
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, SponsorStudyKitReportReadModel).kitReports;
                    return kitData;
                }
                return null;
            })
            );
    }

    //admin
    public getAdminKitExpirationReport(protocol: Protocol[], filterParameters: FilterFieldValue[] = null): Observable<KitExpiration[]> {
        let filtersParams = prepareFilterUrlParams(filterParameters, true);
        if (filtersParams && !filterParameters[4].value) {
            filtersParams = filtersParams.append('requestLimit', this.settings.maxNumberOfSponsorLabReports.toString());
        }

        let studyId = 0;
        if (protocol) {
            studyId = protocol[0].id;
        }

        return this.http.get(`${this.settings.apiUrl}/admin/kit-reports/expiration/${encodeURI(studyId.toString())}`, { params: filtersParams })
            .pipe(map((response) => {
                if (response) {
                    let kitData = deserialize(response, SponsorStudyKitExpirationReadModel).kitExpirations;
                    return kitData;
                }
                return null;
            })
            );
    }

    //admin
    public loadAdminFilterItems(study: number): Observable<DocumentFilterItem[]> {
        return this.http.get(
            `${this.settings.apiUrl}/admin/kit-reports/inventory/${encodeURI(study.toString())}/filter-items`
        ).pipe(
            map((r) => deserialize(r, DocumentFilterItemReadModel).documentFilterItems)
        );
    }

    //admin
    public loadAdminExpirationFilterItems(study: number): Observable<DocumentFilterItem[]> {
        return this.http.get(
            `${this.settings.apiUrl}/admin/kit-reports/expiration/${encodeURI(study.toString())}/filter-items`
        ).pipe(
            map((r) => deserialize(r, DocumentFilterItemReadModel).documentFilterItems)
        );
    }

    public getHelpText(): string {
        return `<p style="font-family:Open Sans, Helvetica Neue, sans-serif;"><b>Status</b> : Value representing the status of a kit type. Kits can be Active, Inactive or On Hold.
                        <br><b>Pending Shipment</b> : Count of kits pending shipment.
                        <br><b>Shipped to Site</b> : Count of kits shipped to the investigator site.
                        <br><b>Received by Lab</b> : Number of used kits received back by Labcorp.
                        <br><b>Adjusted by Site</b> : Number of kits removed from or added to an investigator site’s inventory count.
                        <br><b>On Hand</b> : Number of kits currently at the site.  This number may also reflect kits that have expired that Labcorp has not been notified about.
                        <br><b>Last Shipment Quantity</b> : Number of kits in most recent shipment to the site.
                        <br><b>Last Shipment Date</b> : Date of the most recent shipment to the site.</p>`;
    }

}